import React, { useState, useEffect, useMemo, useRef } from "react";
import { ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline'
import { getDocumentsApi, downloadDocumentApi, getDocumentTypesApi, DocumentTypeInfo } from '../../../../apis/apis';
import { pushToast } from "../../../../components/Toaster/Toaster.slice";
import Button from "../../../../components/Form/Button";
import Table from "../../../../components/Table/Table";
import { formatUTCDate } from "../../../../util/string";
import { DocumentMetadata, GetDocumentsData } from '../../../../apis/types'
import { useAppDispatch, useAppSelector } from "../../../../shared/redux/hooks";
import UploadDocumentModal from './UploadDocumentModal'
import { useTabHeaderActions } from './TabPanel'

export interface TabDocumentsProps {
  title: string
  badge?: number | string
  description?: string
  tenantAccountId: string
}

const TabDocuments: React.FC<TabDocumentsProps> = (props) => {
  const [documents, setDocuments] = useState<GetDocumentsData | null>(null)
  const [loading, setLoading] = useState(true)
  const [openingIds, setOpeningIds] = useState<Set<string>>(new Set())
  const [currentPage, setCurrentPage] = useState(0)
  const [showUploadModal, setShowUploadModal] = useState(false)
  const [replaceTarget, setReplaceTarget] = useState<DocumentMetadata | null>(null)
  const [documentTypes, setDocumentTypes] = useState<DocumentTypeInfo[]>([])
  const dispatch = useAppDispatch()
  const { permissions } = useAppSelector((state) => state.app)
  const hasUploadPermission = permissions?.includes('account:manager:documents:upload') ?? false
  const openUploadRef = useRef(() => setShowUploadModal(true))
  openUploadRef.current = () => setShowUploadModal(true)

  const uploadHeaderAction = useMemo(
    () => (
      <Button onClick={() => openUploadRef.current()} size={2}>
        Upload Document
      </Button>
    ),
    []
  )
  useTabHeaderActions(uploadHeaderAction, hasUploadPermission)

  const labelMap = useMemo(() => Object.fromEntries(documentTypes.map((t) => [t.type, t.label])), [documentTypes])
  const replaceableTypes = useMemo(() => new Set(documentTypes.filter((t) => t.replaceable).map((t) => t.type)), [documentTypes])
  const uploadableOptions = useMemo(
    () => Object.fromEntries(documentTypes.filter((t) => t.uploadable).map((t) => [t.type, t.label])),
    [documentTypes]
  )

  useEffect(() => {
    getDocumentTypesApi()
      .then((data) => setDocumentTypes(Array.isArray(data) ? data : []))
      .catch((err) => console.error('Failed to load document types:', err))
  }, [])

  useEffect(() => {
    loadDocuments(currentPage)
  }, [props.tenantAccountId, currentPage])

  useEffect(() => {
    setCurrentPage(0)
    setShowUploadModal(false)
    setReplaceTarget(null)
  }, [props.tenantAccountId])

  const loadDocuments = async (page: number = 0) => {
    try {
      setLoading(true)
      const response: GetDocumentsData = await getDocumentsApi(props.tenantAccountId, {
        page,
        resultsPerPage: 20,
      })
      setDocuments(response)
    } catch (error) {
      console.error('Error loading documents:', error)
      dispatch(
        pushToast({
          message: 'Failed to load documents',
          type: 'error',
        })
      )
    } finally {
      setLoading(false)
    }
  }

  const handleView = async (documentMetadata: DocumentMetadata) => {
    try {
      setOpeningIds((prev) => new Set(prev).add(documentMetadata.id))

      // Extract document ID from downloadUrl instead of using the prefixed id
      const documentId = documentMetadata.downloadUrl.split('/').pop()!

      const { data, filename } = await downloadDocumentApi(props.tenantAccountId, documentId)

      const blob = new Blob([data], { type: 'application/pdf' })
      const url = window.URL.createObjectURL(blob)
      window.open(url, '_blank')

      dispatch(
        pushToast({
          message: `Opened ${filename}`,
          type: 'success',
        })
      )
    } catch (error) {
      console.error('Error opening document:', error)
      dispatch(
        pushToast({
          message: 'Failed to open document',
          type: 'error',
        })
      )
    } finally {
      setOpeningIds((prev) => {
        const newSet = new Set(prev)
        newSet.delete(documentMetadata.id)
        return newSet
      })
    }
  }

  const handlePaginatorClick = (page: number): void => {
    setCurrentPage(page)
  }

  const colConfig = [
    {
      label: 'Type',
      render: (record: DocumentMetadata) => <span className="text-sm font-medium">{labelMap[record.type] ?? record.type}</span>,
    },
    {
      label: 'Name',
      render: (record: DocumentMetadata) => <span className="font-medium text-gray-900 dark:text-gray-100">{record.name}</span>,
    },
    {
      label: 'Modified Date',
      render: (record: DocumentMetadata) => <span className="text-sm text-gray-600 dark:text-gray-400">{formatUTCDate(new Date(record.modifiedDate))}</span>,
    },
    {
      label: 'Actions',
      skipRowLink: true,
      overflowVisible: true,
      render: (record: DocumentMetadata) => (
        <div className="flex gap-2">
          <Button onClick={() => handleView(record)} disabled={openingIds.has(record.id)} size={2}>
            <span className="inline-flex items-center gap-1.5">
              {openingIds.has(record.id) ? 'Opening...' : 'View'}
              {!openingIds.has(record.id) && <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" aria-hidden />}
            </span>
          </Button>
          {hasUploadPermission && replaceableTypes.has(record.type) && (
            <Button onClick={() => setReplaceTarget(record)} size={2} color="slate">
              Replace
            </Button>
          )}
        </div>
      ),
    },
  ]

  if (loading && documents === null) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  const reloadAfterMutation = () => {
    if (currentPage === 0) {
      loadDocuments(0)
    } else {
      setCurrentPage(0)
    }
  }

  return (
    <>
      {loading ? (
        <div className="flex justify-center items-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      ) : (
        <div className="space-y-4">
          {!documents || documents.results.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-500 dark:text-gray-400">No other documents found for this tenant account.</p>
            </div>
          ) : (
            <Table
              compact
              stickyHeader
              scrollShell={{ heightClassName: 'max-h-64 sm:max-h-80 md:max-h-96 lg:max-h-[45vh]' }}
              data={documents}
              colConfig={colConfig}
              handlePaginatorClick={handlePaginatorClick}
            />
          )}
        </div>
      )}

      <UploadDocumentModal
        isOpen={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        tenantAccountId={props.tenantAccountId}
        onUploadSuccess={reloadAfterMutation}
        documentTypeOptions={uploadableOptions}
      />

      <UploadDocumentModal
        isOpen={replaceTarget !== null}
        onClose={() => setReplaceTarget(null)}
        tenantAccountId={props.tenantAccountId}
        onUploadSuccess={reloadAfterMutation}
        mode="replace"
        documentId={replaceTarget?.id}
        documentTypeOptions={uploadableOptions}
      />
    </>
  )
};

export default TabDocuments;
